import { ReactNode, useMemo } from 'react';

import { Content, Root, Trigger, Item, Separator, MenuContentProps } from '@radix-ui/react-dropdown-menu';
import { twMerge } from 'tailwind-merge';

import { Icon, IconNames } from '../Icon';
import { Text } from '../Text';

import { MenuContext, SIZES, Size } from './config';
import { MenuOption } from './MenuOption';

interface MenuProps extends MenuContentProps {
  readonly open?: boolean;
  readonly disabled?: boolean;
  readonly size?: Size;
  readonly icon?: IconNames;
  readonly trigger?: ReactNode;
  readonly onOpenChange?: (open: boolean) => void;
  readonly className?: string;
  readonly children?: ReactNode;
}

function Menu({ disabled, icon, size = 'm', trigger, open, onOpenChange, className, children, ...props }: MenuProps) {
  const sizing = SIZES[size];
  const context = useMemo(() => {
    return {
      size,
      disabled: !!disabled,
      icon,
    };
  }, [size, disabled, icon]);

  return (
    <Root open={open} onOpenChange={onOpenChange}>
      {typeof trigger === 'string' ? (
        <Trigger
          disabled={disabled}
          className={twMerge(
            'flex w-full items-center',
            'border',
            'focus-ring',
            'duration-300 ease-in-out',
            'hover:border-neutral-400',
            sizing.container,
            sizing.rounding,
            disabled ? 'border-neutral-200 bg-neutral-100 fill-neutral-300' : ' border-neutral-300 bg-white fill-neutral-400',
            className,
          )}
        >
          {icon ? <Icon icon={icon} size={sizing.icon} /> : null}
          <Text
            as="span"
            variant={sizing.variant}
            color="primary"
            className={twMerge(sizing.content, 'w-fit truncate text-left')}
            title={trigger}
          >
            {trigger}
          </Text>
          <Icon icon="chevron-down" size={sizing.icon} className="ml-auto" />
        </Trigger>
      ) : (
        <Trigger disabled={disabled} className={className} asChild>
          {trigger}
        </Trigger>
      )}
      <Content
        {...props}
        sideOffset={8}
        className={twMerge(
          'flex flex-col',
          'max-h-56 overflow-auto',
          'gap-1',
          'bg-neutral-50',
          'shadow-md',
          sizing.rounding,
          sizing.option.container,
          'cursor-pointer focus:outline-none',
        )}
      >
        <MenuContext.Provider value={context}>{children}</MenuContext.Provider>
      </Content>
    </Root>
  );
}

Menu.Option = MenuOption;
Menu.Item = Item;
Menu.Separator = Separator;

export { Menu };
